/**
 * Statewide base shared by the civil wizards: a checklist builder that takes court-layer
 * details or turns them into "verify" items, case classification and first paper fees,
 * and day counting under CCP 12, 12a and 12c on the court's calendar.
 *
 * Nothing here is about a particular court. Court specifics come from src/courts/ through
 * courtDetail().
 */
import { addDays, fmt, fmtISO, iso, parse, todayISO } from "./courtdays.js";
import { courtDetail } from "./courtlayer.js";

export const opts = (o) => Object.entries(o).map(([v, l]) => ({ v, l }));

/* ------------------------------------------------------------ checklist */
/**
 * Collects a wizard's output. Items are { key, text, cite, note, forms }, where forms are
 * forms.json ids the checklist links to.
 */
export function checklist() {
  const sections = [];
  const flags = [];
  const rows = [];
  const verify = [];
  const c = {
    section(id, title, blurb) {
      const s = { id, title, blurb: blurb || "", items: [] };
      sections.push(s);
      return s;
    },
    item(s, key, text, cite, note, forms) {
      s.items.push({ key: `${s.id}.${key}`, text, cite: cite || "", note: note || "", forms: forms || [] });
    },
    /**
     * The court's detail as a checklist item. Without one, `fallback` (a statewide
     * { text, cite, forms }) is used if given, and a "verify" item names the court.
     */
    court(s, key, court, path, what, fallback) {
      const d = courtDetail(court, path, what);
      if (d.known) c.item(s, key, d.text, d.cite, "", d.forms);
      else {
        if (fallback) c.item(s, key, fallback.text, fallback.cite, fallback.note, fallback.forms);
        c.verify(d.verify);
      }
      return d;
    },
    flag(t, b, m) { flags.push({ t, b, m }); },
    row(k, v, sub) { rows.push([k, v, sub]); },
    verify(v) { if (v && !verify.includes(v)) verify.push(v); },
    /** Verify items for the dates between two days that data/ doesn't settle for this court. */
    dates(court, from, to) { court.calendar.notes(from, to).forEach((n) => c.verify(n)); },
    result(title) {
      return { title: title || "Your checklist", flags, rows, sections: sections.filter((s) => s.items.length), verify };
    },
  };
  return c;
}

/* ----------------------------------------------------- classification, fees */
export const CLASS_TXT = {
  unlimited: "Unlimited civil (more than $35,000)",
  limitedOver10k: "Limited civil, more than $10,000 up to $35,000",
  limitedUpTo10k: "Limited civil, $10,000 or less",
};
export const CLASS_OPTS = [
  { v: "unlimited", l: CLASS_TXT.unlimited },
  { v: "limitedOver10k", l: CLASS_TXT.limitedOver10k, h: "Limited civil: amount in controversy $35,000 or less, exclusive of attorney's fees, interest and costs (CCP 85)." },
  { v: "limitedUpTo10k", l: CLASS_TXT.limitedUpTo10k },
];
export const isLimited = (cls) => cls === "limitedOver10k" || cls === "limitedUpTo10k";

// Statewide Civil Fee Schedule, effective January 1, 2026.
const STATEWIDE_FEE = { unlimited: 435, limitedOver10k: 370, limitedUpTo10k: 225, udLimitedOver10k: 385, udLimitedUpTo10k: 240 };
const FEE_CITE = {
  plaintiff: {
    unlimited: "GC 70611; Fee Schedule item 1",
    limitedOver10k: "GC 70613(a); Fee Schedule item 7",
    limitedUpTo10k: "GC 70613(b); Fee Schedule item 10",
    udLimitedOver10k: "GC 70613(a); CCP 1161.2; Fee Schedule item 8",
    udLimitedUpTo10k: "GC 70613(b); CCP 1161.2; Fee Schedule item 11",
  },
  defendant: {
    unlimited: "GC 70612; Fee Schedule item 5",
    limitedOver10k: "GC 70614(a); Fee Schedule item 9",
    limitedUpTo10k: "GC 70614(b); Fee Schedule item 12",
  },
};

/**
 * The first paper fee for a filer ("plaintiff" or "defendant") in a case class. A plaintiff in a
 * limited unlawful detainer pays $15 more (CCP 1161.2); a defendant's first paper in an unlawful
 * detainer is the ordinary amount (Fee Schedule items 5, 9, 12). Uses the court's fees.firstPaper
 * detail where it has one (a local surcharge).
 * Returns { amount, cite, local } where local is the court's Detail if it applied.
 */
export function firstPaperFee(court, cls, { filer = "plaintiff", ud = false } = {}) {
  const key = filer === "plaintiff" && ud && isLimited(cls) ? `ud${cls[0].toUpperCase()}${cls.slice(1)}` : cls;
  const cite = FEE_CITE[filer][key];
  const d = courtDetail(court, "fees.firstPaper", "fee schedule");
  if (d.known && d.value && d.value[key] != null) return { amount: d.value[key], cite: `${cite}; ${d.cite}`, local: d };
  return { amount: STATEWIDE_FEE[key], cite, local: null };
}
export const money = (n) => `$${n.toLocaleString("en-US")}`;

/* ------------------------------------------------------------ day counting */
/**
 * The last day of a period of `days` calendar days after `fromISO` (CCP 12: exclude the first
 * day, include the last). A last day that is not a court day moves to the next court day
 * (CCP 12a, CRC 1.10(b)). Returns { date, moved }.
 */
export function lastDayAfter(court, fromISO, days) {
  const raw = addDays(parse(fromISO), days);
  const date = court.calendar.onOrNextCourtDay(raw);
  return { date, raw, moved: iso(date) !== iso(raw) };
}

/** The same, for a period of years (CCP 583.210's three years): the same date `years` later. */
export function lastDayAfterYears(court, fromISO, years) {
  const from = parse(fromISO);
  const raw = new Date(from.getFullYear() + years, from.getMonth(), from.getDate());
  if (raw.getMonth() !== from.getMonth()) raw.setDate(0); // February 29 to February 28
  const date = court.calendar.onOrNextCourtDay(raw);
  return { date, raw, moved: iso(date) !== iso(raw) };
}

/**
 * The last day to act `days` calendar days before an event, counting backward and excluding
 * the event day (CCP 12c(a)). When that day is not a court day, `safe` is the court day before
 * it; the rules in data/ don't say which way such a day moves when counting backward.
 * Returns { date, safe, moved }.
 */
export function lastDayBefore(court, eventISO, days) {
  const date = addDays(parse(eventISO), -days);
  const safe = court.calendar.isCourtDay(date) ? date : court.calendar.prevCourtDay(date);
  return { date, safe, moved: iso(date) !== iso(safe) };
}

export const BACKWARD_NOTE = "When a deadline counted backward from a hearing or conference lands on a weekend or court holiday, the downloaded rules don't say whether it moves earlier or later (CCP 12a and CRC 1.10(b) extend periods forward). The checklist uses the court day before, which is safe either way.";

export { fmt, fmtISO, iso, parse, addDays, todayISO };
